import { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Send, Phone, Mail, MessageCircle, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      setStatus('success');
      setForm({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setStatus(null), 5000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <section id="contact" className="py-24 md:py-32 lg:py-40 bg-[#0f1115] relative" data-testid="contact-section">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#C8B598]/[0.03] to-transparent pointer-events-none" />

      <div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Info */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C8B598]">
                {t('contact.overline')}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0] leading-tight">
                {t('contact.title')}
              </h2>
              <p className="text-base font-light leading-relaxed text-[#9CA3AF]">
                {t('contact.subtitle')}
              </p>
            </div>

            <div className="space-y-4 pt-4">
              <a
                href="tel:+34699701015"
                className="flex items-center gap-4 p-4 bg-[#050505] border border-white/5 hover:border-[#C8B598]/20 transition-colors group"
                data-testid="contact-phone"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#C8B598]/10 border border-[#C8B598]/30">
                  <Phone className="w-4 h-4 text-[#C8B598]" />
                </div>
                <div>
                  <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">{lang === 'en' ? 'Phone' : 'Tel\u00E9fono'}</p>
                  <p className="text-sm text-[#F5F5F0] group-hover:text-[#C8B598] transition-colors">+34 699 70 10 15</p>
                </div>
              </a>

              <a
                href="mailto:info@villa259.com"
                className="flex items-center gap-4 p-4 bg-[#050505] border border-white/5 hover:border-[#C8B598]/20 transition-colors group"
                data-testid="contact-email"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#C8B598]/10 border border-[#C8B598]/30">
                  <Mail className="w-4 h-4 text-[#C8B598]" />
                </div>
                <div>
                  <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">Email</p>
                  <p className="text-sm text-[#F5F5F0] group-hover:text-[#C8B598] transition-colors">info@villa259.com</p>
                </div>
              </a>

              <a
                href="https://wa.me/34699701015"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-[#050505] border border-white/5 hover:border-[#C8B598]/20 transition-colors group"
                data-testid="contact-whatsapp"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#C8B598]/10 border border-[#C8B598]/30">
                  <MessageCircle className="w-4 h-4 text-[#C8B598]" />
                </div>
                <div>
                  <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">WhatsApp</p>
                  <p className="text-sm text-[#F5F5F0] group-hover:text-[#C8B598] transition-colors">+34 699 70 10 15</p>
                </div>
              </a>
            </div>

            {/* Architect credit */}
            <div className="pt-4 border-t border-white/5">
              <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">
                {lang === 'en' ? 'Architectural Project' : 'Proyecto Arquitect\u00F3nico'}
              </p>
              <a
                href="https://www.ismaelmerida.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#C8B598] hover:text-[#E2D1B8] transition-colors"
                data-testid="architect-website-link"
              >
                Ismael M&#233;rida Arquitectos
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">
                  {t('contact.form.name')}
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange('name')}
                  className="w-full bg-transparent border-b border-white/20 pb-3 pt-2 text-[#F5F5F0] placeholder-white/20 focus:outline-none focus:border-[#C8B598] transition-colors rounded-none font-light"
                  placeholder={t('contact.form.name')}
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">
                  {t('contact.form.email')}
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange('email')}
                  className="w-full bg-transparent border-b border-white/20 pb-3 pt-2 text-[#F5F5F0] placeholder-white/20 focus:outline-none focus:border-[#C8B598] transition-colors rounded-none font-light"
                  placeholder={t('contact.form.email')}
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">
                  {t('contact.form.phone')}
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  className="w-full bg-transparent border-b border-white/20 pb-3 pt-2 text-[#F5F5F0] placeholder-white/20 focus:outline-none focus:border-[#C8B598] transition-colors rounded-none font-light"
                  placeholder={t('contact.form.phone')}
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#9CA3AF] mb-2">
                  {t('contact.form.message')}
                </label>
                <textarea
                  value={form.message}
                  onChange={handleChange('message')}
                  rows={4}
                  className="w-full bg-transparent border-b border-white/20 pb-3 pt-2 text-[#F5F5F0] placeholder-white/20 focus:outline-none focus:border-[#C8B598] transition-colors rounded-none font-light resize-none"
                  placeholder={t('contact.form.message')}
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C8B598] text-[#050505] font-medium tracking-wide transition-all duration-300 hover:bg-[#E2D1B8] hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                data-testid="contact-submit"
              >
                {status === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {t('contact.form.submit')}
              </button>

              {status === 'success' && (
                <p className="text-sm text-green-400 text-center" data-testid="contact-success-message">
                  {t('contact.form.success')}
                </p>
              )}
              {status === 'error' && (
                <p className="text-sm text-red-400 text-center" data-testid="contact-error-message">
                  {t('contact.form.error')}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
